import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

// Landing point of a Super Admin association switch (associationService.switchTo):
// exchanges the one-time token in the URL fragment for a session on this address,
// then opens the portal. The fragment never reaches a server and is cleared first.
const SwitchPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [error, setError] = useState(null);
  const [verified, setVerified] = useState(false);

  // Open the portal only once AuthContext has loaded the account; navigating right
  // after verifyOtp raced the profile load and bounced to the landing page.
  useEffect(() => {
    if (verified && isAuthenticated) navigate('/portal', { replace: true });
  }, [verified, isAuthenticated, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const tokenHash = params.get('token_hash');
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    if (!tokenHash) {
      navigate('/portal', { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { error: verifyError } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'magiclink' });
        if (verifyError) throw verifyError;
        if (!cancelled) setVerified(true);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'This switch link is no longer valid.');
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  return (
    <main className="min-h-screen bg-main-bg flex items-center justify-center px-6">
      <section className="w-full max-w-sm bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 p-8 text-center">
        {error ? (
          <>
            <span className="material-symbols-outlined text-secondary" style={{ fontSize: '36px' }}>link_off</span>
            <h1 className="text-xl font-bold text-primary-dark mt-3 mb-2">Switch did not complete</h1>
            <p className="text-sm font-medium text-slate-500 mb-6">{error} Sign in here, or switch again from your portal.</p>
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              className="bg-secondary text-white px-8 py-3.5 rounded-full font-bold hover:brightness-110 active:scale-95 transition-all"
            >
              Go to sign in
            </button>
          </>
        ) : (
          <>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary mx-auto" />
            <p className="text-sm font-bold text-slate-500 mt-4">Opening association…</p>
          </>
        )}
      </section>
    </main>
  );
};

export default SwitchPage;
